import {
  Component,
  OnInit,
  inject,
  OnDestroy,
  Input,
  effect,
  ChangeDetectorRef,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormGroupDirective,
  Validators,
  FormControl,
  NgForm,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatRadioModule } from "@angular/material/radio";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatDialogModule, MatDialog } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { Store } from "@ngrx/store";
import { Actions, ofType } from "@ngrx/effects";
import { Subject, take, takeUntil, debounceTime } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  selectActivePacket,
  selectIsSaving,
  selectTraditionalPI,
  selectMvpPI,
  selectRelatedOrganizations,
  selectEffectiveQuestionnaire,
} from "@store/selectors/mips-packet/mips-packet.selector";
import { selectUserAdminFilteredOrgs } from "@store/selectors/user-admin/user-admin.selector";
import { MipsPacketActions } from "@store/actions/mips-packet/mips-packet.actions";
import {
  PiMeasure,
  PiCategoryGroup,
  ActiveEngagementLevel,
  PiDocument,
} from "@models/mips-stepper.model";
import {
  PI_MEASURES_2025,
  PI_MEASURES_2026,
  PI_CATEGORY_ORDER,
  PI_CATEGORY_DISPLAY_NAMES,
} from "./pi-measures-data";
import {
  getMipsPerformanceDates,
  isPdmpExclusionMissing,
  PDMP_EXCLUSION_REQUIRED_MESSAGE,
} from "../../constants/mips-constants";
import { MAX_FILE_SIZE } from '@constants/file-upload';
import { MipsApiService } from "@services/mips-api.service";
import { openBlobInNewTab } from "../../utils/blob-viewer.util";
import { PiAttestationModalComponent } from "@components/modals/pi-attestation-modal/pi-attestation-modal.component";
import { PiExclusionModalComponent } from "@components/modals/pi-exclusion-modal/pi-exclusion-modal.component";
import { ConfirmationModalComponent } from "@components/confirmation-modal/confirmation-modal.component";
import { MipsReweightingService } from "@services/mips-reweighting.service";
import { MipsValidationService } from "@services/mips-validation.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ErrorStateMatcher } from "@angular/material/core";

export class CustomErrorStateMatcher implements ErrorStateMatcher {
  constructor(private isInvalid: () => boolean) {}

  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    return this.isInvalid();
  }
}

@Component({
  selector: "app-mips-pi",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatRadioModule,
    MatInputModule,
    MatFormFieldModule,
    MatDialogModule,
    MatExpansionModule,
    MatTooltipModule,
    MatSelectModule,
    MatDatepickerModule,
  ],
  templateUrl: "./mips-pi.component.html",
  styleUrls: ["./mips-pi.component.css"],
})
export class MipsPromotingInteroperabilityComponent
  implements OnInit, OnDestroy
{
  @Input() parentForm!: FormGroup;

  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly ctrlContainer = inject(FormGroupDirective);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly reweightingService = inject(MipsReweightingService);
  private readonly validationService = inject(MipsValidationService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly mipsApiService = inject(MipsApiService);
  private readonly actions$ = inject(Actions);

  questionnaire = toSignal(this.store.select(selectEffectiveQuestionnaire));
  multiPacketMode = toSignal(
      this.store.select((state: any) => state.mipsPacket?.multiPacketMode),
    );
  traditionalPI = toSignal(this.store.select(selectTraditionalPI));
  mvpPI = toSignal(this.store.select(selectMvpPI));

  reweighting = this.reweightingService.getReweightingSignal();

  private readonly destroy$ = new Subject<void>();
  private readonly measureChanged$ = new Subject<void>();
  private readonly measurePdfResourcesCancel$ = new Subject<void>();

  readonly minDate = getMipsPerformanceDates().MIN_DATE;
  readonly maxDate = getMipsPerformanceDates().MAX_DATE;
  dateRangeInvalid = false;
  startDateBeforeEndDateError = false;

  performanceStartDateInvalid = false;
  performanceEndDateInvalid = false;
  startDateErrorMatcher!: ErrorStateMatcher;
  endDateErrorMatcher!: ErrorStateMatcher;
  measureValidationErrors = new Map<string, string>();
  hasPdmpExclusionError = false;
  readonly pdmpExclusionErrorMessage = PDMP_EXCLUSION_REQUIRED_MESSAGE;

  currentPacket = toSignal(this.store.select(selectActivePacket));
  isScoreCalculating = toSignal(this.store.select(selectIsSaving), { initialValue: false });
  relatedOrganizations = toSignal(this.store.select(selectRelatedOrganizations), { initialValue: [] });

  piForm!: FormGroup;
  measures: PiMeasure[] = [];
  categories: PiCategoryGroup[] = [];
  attestationData: any = null;
  performanceStartDate: Date | null = null;
  performanceEndDate: Date | null = null;
  fileUploadExpanded = false;

  private dataLoaded = false;
  private loadedPacketId: number | undefined = undefined;

  private lastLoadedMeasuresHash: string = "";
  private lastLoadedPerformanceYear: number | undefined = undefined;

  constructor() {
    effect(() => {
      const packet = this.currentPacket();
      const performanceYear = packet?.performanceYear;
      if (performanceYear !== undefined && performanceYear !== this.lastLoadedPerformanceYear) {
        this.lastLoadedPerformanceYear = performanceYear;
        this.loadMeasures();
        this.organizeCategories();
      }

      const mode = this.multiPacketMode();
      const traditionalPI = this.traditionalPI();
      const mvpPI = this.mvpPI();
      const hasData = mode?.enabled
        ? ((traditionalPI?.measures?.length || 0) > 0 || (mvpPI?.measures?.length || 0) > 0)
        : (packet?.promotingInteroperability?.measures?.length || 0) > 0;

      if (hasData && this.measures.length > 0) {
        let backendMeasuresHash: string;
        if (mode?.enabled) {
          backendMeasuresHash = JSON.stringify({
            traditional: traditionalPI?.measures || [],
            mvp: mvpPI?.measures || [],
          });
        } else {
          backendMeasuresHash = JSON.stringify({
            packetId: packet?.id,
            measures: packet?.promotingInteroperability?.measures || [],
          });
        }

        if (backendMeasuresHash !== this.lastLoadedMeasuresHash) {
          this.lastLoadedMeasuresHash = backendMeasuresHash;

          if (
            !mode?.enabled &&
            this.dataLoaded &&
            packet?.id !== undefined &&
            packet.id === this.loadedPacketId
          ) {
            this.applyScoresFromPacket(packet?.promotingInteroperability);
          } else {
            if (mode?.enabled) {
              this.loadFromPacket(traditionalPI, mvpPI);
            } else {
              this.loadFromPacket(packet?.promotingInteroperability);
            }

            this.loadDatesFromPacket();
          }
        }
      } else if (this.measures.length > 0) {
        if (this.lastLoadedMeasuresHash !== "") {
          this.lastLoadedMeasuresHash = "";
          this.resetMeasures();
          this.loadDatesFromPacket();
          this.updateCategoryPoints();
          this.updateCategoryCompletionStatus();
          this.updateParentFormValidity();
          this.cdr.markForCheck();
        }
      }

      setTimeout(() => {
        this.updateParentFormValidity();
        this.cdr.markForCheck();
      });
    });
  }

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  ngOnInit(): void {
    this.piForm = this.fb.group({
      measuresComplete: [false, Validators.requiredTrue],
    });

    this.loadMeasures();
    this.organizeCategories();

    setTimeout(() => {
      this.parentForm = this.ctrlContainer.form;
      if (this.parentForm) {
        this.parentForm.addControl("pi", this.piForm);
      }

      const packet = this.currentPacket();
      if (packet?.promotingInteroperability) {
        this.loadFromPacket();
      }

      this.loadDatesFromPacket();
      this.updateParentFormValidity();
    });

    this.piForm.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateParentFormValidity();
    });

    this.measureChanged$
      .pipe(debounceTime(2000), takeUntil(this.destroy$))
      .subscribe(() => {
        this.savePiData();
      });

    this.actions$
      .pipe(
        ofType(
          MipsPacketActions.loadExistingPacketSuccess,
          MipsPacketActions.getOrCreatePacketSuccess,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.loadedPacketId = undefined;
      });

    this.startDateErrorMatcher = new CustomErrorStateMatcher(
      () =>
        this.performanceStartDateInvalid || this.startDateBeforeEndDateError,
    );

    this.endDateErrorMatcher = new CustomErrorStateMatcher(
      () => this.performanceEndDateInvalid,
    );
  }

  ngOnDestroy(): void {
    if (this.parentForm) {
      this.parentForm.removeControl("pi");
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================================
  // GETTERS - DISPLAY VALUES
  // ============================================================================

  get practice(): string {
    const practiceId = this.currentPacket()?.practice || "";
    if (!practiceId) return "";

    let orgName = practiceId;
    this.store
      .select(selectUserAdminFilteredOrgs)
      .pipe(take(1))
      .subscribe((orgs) => {
        if (orgs) {
          const org = orgs.find((o) => o.id.toString() === practiceId);
          if (org) orgName = org.name;
        }
      });

    return orgName;
  }

  get rollupTooltip(): string {
    const orgs = [this.practice, ...this.relatedOrganizations().map(o => o.organizationName)];
    return orgs.filter(Boolean).join(", ");
  }

  get tin(): string {
    return this.currentPacket()?.tinDetails?.tin || "N/A";
  }

  get performanceDuration(): string {
    const packet = this.currentPacket();
    const start = packet?.tinDetails?.performanceStartDate;
    const end = packet?.tinDetails?.performanceEndDate;

    if (start && end) {
      return `${this.formatDate(start)} - ${this.formatDate(end)}`;
    }
    return "";
  }

  // ============================================================================
  // GETTERS - VALIDATION & SCORING
  // ============================================================================

  get cehrtValidated(): number {
    return this.currentPacket()?.settings?.cehrtId ? 1 : 0;
  }

  get attestationsComplete(): boolean {
    return this.currentPacket()?.promotingInteroperability?.attestationsComplete ?? false;
  }

  get allBlockingComplete(): boolean {
    return this.attestationsComplete;
  }

  get allRequiredComplete(): boolean {
    return this.currentPacket()?.promotingInteroperability?.allRequiredComplete ?? false;
  }

  get canProceedToNextStep(): boolean {
    return (
      this.allBlockingComplete &&
      this.measureValidationErrors.size === 0 &&
      !this.hasPdmpExclusionError
    );
  }

  get estimatedPoints(): number {
    return this.currentPacket()?.promotingInteroperability?.estimatedPoints ?? 0;
  }

  get estimatedBonus(): number {
    return this.currentPacket()?.promotingInteroperability?.estimatedBonus ?? 0;
  }

  get estimatedTotal(): number {
    return this.currentPacket()?.promotingInteroperability?.estimatedTotal ?? 0;
  }

  get estimatedWeight(): number {
    return this.currentPacket()?.promotingInteroperability?.estimatedWeight ?? 0;
  }

  get reweightingTooltip(): string {
    return this.reweighting().explanationTooltip;
  }

  get showReweightingIndicator(): boolean {
    return this.reweighting().isReweighted;
  }

  get bothHieExclusionsClaimed(): boolean {
    const hie1 = this.measures.find((m) => m.measureId === "PI_HIE_1");
    const hie4 = this.measures.find((m) => m.measureId === "PI_HIE_4");
    return !!(hie1?.exclusionClaimed && hie4?.exclusionClaimed);
  }

  get hieAlternativeSelected(): boolean {
    const hie5 = this.measures.find((m) => m.measureId === "PI_HIE_5");
    const hie6 = this.measures.find((m) => m.measureId === "PI_HIE_6");
    return !!(hie5?.completed || hie6?.completed);
  }

  get incompleteMeasuresCount(): number {
    return this.currentPacket()?.promotingInteroperability?.incompleteMeasureIds?.length ?? 0;
  }

  get hasIncompleteMeasures(): boolean {
    return this.incompleteMeasuresCount > 0;
  }

  get firstSelectedBonusMeasure(): string | null {
    const bonusMeasures = this.measures.filter(m => m.isBonusMeasure).map(m => m.measureId);

    for (const measureId of bonusMeasures) {
      const measure = this.measures.find((m) => m.measureId === measureId);
      if (measure?.attestation === true) {
        return measureId;
      }
    }

    return null;
  }

  // ============================================================================
  // GETTERS - FILE UPLOAD
  // ============================================================================

  get piDocuments(): PiDocument[] {
    return this.currentPacket()?.promotingInteroperability?.documents || [];
  }

  get canUploadMoreDocuments(): boolean {
    return this.piDocuments.length < 10;
  }

  // ============================================================================
  // VALIDATION HELPERS
  // ============================================================================

  getMeasureError(measureId: string): string | null {
    return this.measureValidationErrors.get(measureId) || null;
  }

  hasMeasureError(measureId: string): boolean {
    return this.measureValidationErrors.has(measureId);
  }

  private validateNumeratorDenominator(measure: PiMeasure): void {
    if (measure.valueType !== "numerator_denominator") {
      this.measureValidationErrors.delete(measure.measureId);
      return;
    }

    const num = measure.numerator ?? 0;
    const den = measure.denominator ?? 0;

    if (!measure.selected || measure.exclusionClaimed) {
      this.measureValidationErrors.delete(measure.measureId);
      return;
    }

    if (num < 0 || den < 0) {
      this.measureValidationErrors.set(
        measure.measureId,
        "Values cannot be negative",
      );
      return;
    }

    if (num > den && den >= 0) {
      this.measureValidationErrors.set(
        measure.measureId,
        "Numerator cannot exceed denominator",
      );
      return;
    }

    this.measureValidationErrors.delete(measure.measureId);
  }

  private validatePdmpExclusionRule(): void {
    this.hasPdmpExclusionError = isPdmpExclusionMissing(
      this.currentPacket()?.performanceYear,
      this.getClaimedExclusionIds(),
    );
  }

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  private loadMeasures(): void {
    const performanceYear = this.currentPacket()?.performanceYear ?? 2025;
    const source = performanceYear >= 2026 ? PI_MEASURES_2026 : PI_MEASURES_2025;
    this.measures = JSON.parse(JSON.stringify(source));
    this.dataLoaded = false;
    this.loadedPacketId = undefined;
    this.loadMeasurePdfResources(performanceYear);
  }

  private loadMeasurePdfResources(performanceYear: number): void {
    this.measurePdfResourcesCancel$.next();

    this.mipsApiService
      .getPiMeasurePdfResources(performanceYear)
      .pipe(takeUntil(this.measurePdfResourcesCancel$), takeUntil(this.destroy$))
      .subscribe({
        next: (resources) => {
          const urlByMeasureId = new Map(
            resources.map((resource) => [resource.measureId, resource.url]),
          );
          this.measures.forEach((measure) => {
            measure.pdfUrl = urlByMeasureId.get(measure.measureId);
          });
          this.cdr.markForCheck();
        },
        // Reference PDF links are non-critical; measures simply render without a pdf-link button.
        error: (error) => {
          console.error("Failed to load PI measure PDF resources:", error);
        },
      });
  }

  private resetMeasures(): void {
    this.measures.forEach((m) => {
      m.selected = m.isRequired;
      m.completed = false;
      m.attestation = undefined;
      m.numerator = undefined;
      m.denominator = undefined;
      m.performance = 0;
      m.activeEngagementLevel = null;
      m.exclusionClaimed = null;
    });
    this.attestationData = null;
    this.dataLoaded = false;
    this.loadedPacketId = undefined;
    this.performanceStartDate = null;
    this.performanceEndDate = null;
    this.measureValidationErrors.clear();
    this.hasPdmpExclusionError = false;
  }

  private organizeCategories(): void {
    this.categories = [];

    PI_CATEGORY_ORDER.forEach((categoryKey) => {
      const categoryMeasures = this.measures.filter(
        (m) => m.category === categoryKey,
      );

      if (categoryMeasures.length > 0) {
        this.categories.push({
          category: categoryKey,
          displayName: PI_CATEGORY_DISPLAY_NAMES[categoryKey],
          measures: categoryMeasures,
          isExpanded: true,
          isComplete: false,
          estimatedPoints: 0,
        });
      }
    });
  }

  private loadDatesFromPacket(): void {
    this.performanceStartDateInvalid = false;
    this.performanceEndDateInvalid = false;

    const packet = this.currentPacket();
    const piData = packet?.promotingInteroperability as any;
    if (piData?.performanceStart && piData?.performanceEnd) {
      this.performanceStartDate = new Date(piData.performanceStart);
      this.performanceEndDate = new Date(piData.performanceEnd);
      this.cdr.markForCheck();
      return;
    }

    if (!this.performanceStartDate) {
      this.performanceStartDate = getMipsPerformanceDates().MIN_DATE;
    }

    if (!this.performanceEndDate) {
      this.performanceEndDate = getMipsPerformanceDates().MAX_DATE;
    }

    this.cdr.markForCheck();
  }

  private loadFromPacket(traditionalPIData?: any, mvpPIData?: any): void {
    const packet = this.currentPacket();
    const mode = this.multiPacketMode();
    let piData: any;
    if (mode?.enabled) {

      piData = traditionalPIData || mvpPIData;
    } else {
      piData = packet?.promotingInteroperability;
    }

    if (!piData) {
      return;
    }


    if (piData.attestations) {
      this.loadExistingAttestations();
    }

    const allExclusionIds = (piData as any).allExclusionIds || [];

    this.measures.forEach((m) => {
      m.selected = m.isRequired; 
      m.completed = false;
      m.attestation = undefined;
      m.numerator = undefined;
      m.denominator = undefined;
      m.performance = 0;
      m.activeEngagementLevel = null;
      m.exclusionClaimed = null;
    });

    let loadedCount = 0;
    let selectedCount = 0;

    piData.measures?.forEach((savedMeasure: any) => {
      const measure = this.measures.find(
        (m) => m.measureId === savedMeasure.measureId,
      );

      if (measure) {
        loadedCount++;

        if (!measure.isRequired) {
          measure.selected = true;
          selectedCount++;
        } else if (measure.isRequired) {
          selectedCount++;
        }

        measure.completed = savedMeasure.completed ?? false;
        measure.attestation = savedMeasure.attestation;
        measure.numerator = savedMeasure.numerator;
        measure.denominator = savedMeasure.denominator;
        measure.performance = savedMeasure.performance ?? 0;
        measure.activeEngagementLevel = savedMeasure.activeEngagementLevel;
        measure.points = savedMeasure.points ?? 0;
        measure.effectiveMaxPoints = savedMeasure.effectiveMaxPoints;
        measure.redistributedPoints = savedMeasure.redistributedPoints;
        measure.bonusPoints = savedMeasure.bonusPoints;
        measure.showBonusPoints = savedMeasure.showBonusPoints ?? false;

        this.validateNumeratorDenominator(measure);
      }
    });
    this.measures.forEach((m) => {
      if (m.isAlternativeMeasure && m.selected && m.replacedMeasureIds) {
        m.replacedMeasureIds.forEach((replacedId: string) => {
          const replaced = this.measures.find((r) => r.measureId === replacedId);
          if (replaced) {
            replaced.selected = false;
            replaced.completed = false;
          }
        });
      }
    });

    let exclusionCount = 0;
    allExclusionIds.forEach((exclusionId: string) => {
      const parentMeasure = this.measures.find((m) =>
        m.exclusions?.some((ex) => ex.exclusionId === exclusionId),
      );

      if (parentMeasure) {
        exclusionCount++;
        parentMeasure.selected = true;
        parentMeasure.exclusionClaimed = exclusionId;
        parentMeasure.completed = true;
        parentMeasure.attestation = undefined;
        parentMeasure.numerator = undefined;
        parentMeasure.denominator = undefined;
        parentMeasure.performance = 0;
        parentMeasure.activeEngagementLevel = null;
      }
    });

    this.validatePdmpExclusionRule();
    this.updateCategoryPoints();
    this.updateCategoryCompletionStatus();
    this.updateParentFormValidity();
    this.dataLoaded = true;
    this.loadedPacketId = packet?.id;
    this.cdr.markForCheck();
  }

  private applyScoresFromPacket(piData?: any): void {
    this.measures.forEach((measure) => {
      const savedMeasure = piData?.measures?.find(
        (saved: any) => saved.measureId === measure.measureId,
      );

      measure.points = savedMeasure?.points ?? 0;
      measure.effectiveMaxPoints = savedMeasure?.effectiveMaxPoints;
      measure.redistributedPoints = savedMeasure?.redistributedPoints;
      measure.bonusPoints = savedMeasure?.bonusPoints;
      measure.showBonusPoints = savedMeasure?.showBonusPoints ?? false;
    });

    this.updateCategoryPoints();
    this.updateParentFormValidity();
    this.cdr.markForCheck();
  }

  private loadExistingAttestations(): void {
    const packet = this.currentPacket();
    const piData = packet?.promotingInteroperability;
    if (!piData?.attestations) return;

    const toYesNo = (value: boolean | null | undefined): string => {
      if (value === true) return "yes";
      if (value === false) return "no";
      return "";
    };

    this.attestationData = {
      piAuditAttestation: piData.attestations.piAuditAttestation === true,
      oncDirectReview: toYesNo(piData.attestations.oncDirectReview),
      interoperabilityAttestation: toYesNo(
        piData.attestations.interoperabilityAttestation,
      ),
      securityRiskAnalysis: toYesNo(piData.attestations.securityRiskAnalysis),
      securityRiskAnalysisImplemented: toYesNo(piData.attestations.securityRiskAnalysisImplemented),
      saferGuidesAttestation: toYesNo(
        piData.attestations.saferGuidesAttestation,
      ),
    };

  }


  isHieMeasureDisabled(measure: PiMeasure): boolean {
    if (!measure.measureId.startsWith("PI_HIE")) return false;

    if (measure.selected) return false;

    const hie1 = this.measures.find((m) => m.measureId === "PI_HIE_1");
    const hie4 = this.measures.find((m) => m.measureId === "PI_HIE_4");
    const hie5 = this.measures.find((m) => m.measureId === "PI_HIE_5");
    const hie6 = this.measures.find((m) => m.measureId === "PI_HIE_6");

    if (measure.measureId === "PI_HIE_1" || measure.measureId === "PI_HIE_4") {
      return !!(hie5?.selected || hie6?.selected);
    }

    if (measure.measureId === "PI_HIE_5") {
      return !!(hie1?.selected || hie4?.selected || hie6?.selected);
    }

    if (measure.measureId === "PI_HIE_6") {
      return !!(hie1?.selected || hie4?.selected || hie5?.selected);
    }

    return false;
  }

  isConditionallyRequired(measureId: string): boolean {
    return (
      this.bothHieExclusionsClaimed &&
      (measureId === "PI_HIE_5" || measureId === "PI_HIE_6")
    );
  }

  private updateParentFormValidity(): void {
    if (!this.parentForm) return;

    const excluded = this.validationService.getExcludedCategories(this.questionnaire());
    if (excluded.has("pi")) {
      this.parentForm.setErrors(null);
      return;
    }

    const datesValid =
      !this.performanceStartDateInvalid &&
      !this.performanceEndDateInvalid &&
      !this.dateRangeInvalid;

    const datesProvided = this.performanceStartDate && this.performanceEndDate;
    const noValidationErrors = this.measureValidationErrors.size === 0;

    if (
      this.canProceedToNextStep &&
      datesValid &&
      datesProvided &&
      noValidationErrors
    ) {
      this.parentForm.setErrors(null);
      this.piForm.patchValue({ measuresComplete: true }, { emitEvent: false });
    } else {
      this.parentForm.setErrors({ incomplete: true });
      this.piForm.patchValue({ measuresComplete: false }, { emitEvent: false });
    }
  }

  private updateCategoryPoints(): void {
    this.categories.forEach((category: PiCategoryGroup) => {
      category.estimatedPoints = category.measures.reduce(
        (sum: number, m: PiMeasure) => sum + m.points,
        0,
      );
    });
  }

  private updateCategoryCompletionStatus(): void {
    this.categories.forEach((category: PiCategoryGroup) => {
      const requiredMeasures = category.measures.filter(
        (m: PiMeasure) => m.isRequired,
      );
      if (requiredMeasures.length > 0) {
        category.isComplete = requiredMeasures.every(
          (m: PiMeasure) => m.completed || m.exclusionClaimed,
        );
      } else {
        category.isComplete = category.measures.some(
          (m: PiMeasure) => m.completed,
        );
      }
    });
  }

  private updateMeasureCompletion(measure: PiMeasure): void {
    switch (measure.valueType) {
      case "yes_no":
        if (measure.attestation === true) {
          measure.completed = true;
        } else if (measure.attestation === false) {
          if (measure.exclusions && measure.exclusions.length > 0) {
            measure.completed = !!measure.exclusionClaimed;
          } else {
            measure.completed = false;
          }
        } else {
          measure.completed = false;
        }
        break;

      case "numerator_denominator":
        const hasValidData =
          (measure.numerator || 0) > 0 && (measure.denominator || 0) > 0;
        const hasNoErrors = !this.hasMeasureError(measure.measureId);
        measure.completed = hasValidData && hasNoErrors;
        break;

      case "yes_no_engagement":
        if (measure.measureId === "PI_PHCDRR_3") {
          if (measure.attestation === true || measure.attestation === false) {
            measure.completed = true;
          } else {
            measure.completed = false;
          }
        } else {
          if (measure.attestation === true) {
            measure.completed = !!measure.activeEngagementLevel;
          } else if (measure.attestation === false) {
            if (measure.exclusions && measure.exclusions.length > 0) {
              measure.completed = !!measure.exclusionClaimed;
            } else {
              measure.completed = false;
            }
          } else {
            measure.completed = false;
          }
        }
        break;

      case "attestation":
        measure.completed = measure.attestation === true;
        break;
    }
  }

  // ============================================================================
  // HIE HELPER METHOD
  // ============================================================================

  private getActiveHieMeasure(): string | null {
    const hie1 = this.measures.find((m) => m.measureId === "PI_HIE_1");
    const hie4 = this.measures.find((m) => m.measureId === "PI_HIE_4");
    const hie5 = this.measures.find((m) => m.measureId === "PI_HIE_5");
    const hie6 = this.measures.find((m) => m.measureId === "PI_HIE_6");

    if (hie5?.selected) {
      return "PI_HIE_5";
    }

    if (hie6?.selected) {
      return "PI_HIE_6";
    }

    if (hie1?.selected && hie4?.selected) {
      return "PI_HIE_1";
    }
    return null;
  }

  // ============================================================================
  // DISPLAY HELPERS
  // ============================================================================

  getBonusPointsDisplay(measure: PiMeasure): string {
    if (!measure.isBonusMeasure) return "";
    return (measure.bonusPoints ?? 0) > 0 ? "+5 Bonus" : "+5 Bonus (Available)";
  }

  getAdjustedMaxPoints(measure: PiMeasure): number {
    return measure.effectiveMaxPoints ?? measure.maxPoints ?? 0;
  }

  shouldShowBonusPoints(measureId: string): boolean {
    const measure = this.measures.find((m) => m.measureId === measureId);
    return measure?.showBonusPoints ?? false;
  }

  getMeasurePointsDisplay(measure: PiMeasure): string {
    if (measure.isBonusMeasure) {
      if (!measure.showBonusPoints) {
        return "N/A";
      }
      return `${measure.points}/5`;
    }

    if (this.hasMeasureError(measure.measureId)) {
      return "—";
    }

    if (measure.points === undefined || measure.points === null) {
      return "0";
    }

    return `${measure.points}/${this.getAdjustedMaxPoints(measure)}`;
  }

  hasRedistributedPoints(measure: PiMeasure): boolean {
    return (measure.redistributedPoints ?? 0) > 0;
  }

  getRedistributedPointsAmount(measure: PiMeasure): number {
    return measure.redistributedPoints ?? 0;
  }

  private isPublicHealthFlatBonusApplied(measure: PiMeasure): boolean {
    if (measure.measureId !== "PI_PEA_1") return false;
    const phcdrr1 = this.measures.find((candidate) => candidate.measureId === "PI_PHCDRR_1");
    const phcdrr3 = this.measures.find((candidate) => candidate.measureId === "PI_PHCDRR_3");
    return !!(phcdrr1?.exclusionClaimed && phcdrr3?.exclusionClaimed);
  }

  isBonusMeasure(measureId: string): boolean {
    return this.measures.some(m => m.measureId === measureId && m.isBonusMeasure);
  }

  getRedistributionTooltip(measure: PiMeasure): string {
    const sources: string[] = [];

    if (
      measure.measureId === "PI_PHCDRR_1" ||
      measure.measureId === "PI_PHCDRR_3"
    ) {
      const phcdrr1 = this.measures.find((m) => m.measureId === "PI_PHCDRR_1");
      const phcdrr3 = this.measures.find((m) => m.measureId === "PI_PHCDRR_3");

      const phcdrr1Participating = !!(
        phcdrr1?.selected &&
        phcdrr1?.completed &&
        !phcdrr1?.exclusionClaimed
      );

      const phcdrr3Participating = !!(
        phcdrr3?.selected &&
        phcdrr3?.completed &&
        !phcdrr3?.exclusionClaimed
      );

      if (measure.measureId === "PI_PHCDRR_1") {
        if (phcdrr1Participating && phcdrr3Participating) {
          return "Both Public Health measures completed - receives 12.5 base points + 12.5 redistributed from PI_PHCDRR_3 = 25 total";
        } else if (phcdrr1Participating && !phcdrr3Participating) {
          return "Only PI_PHCDRR_1 participating - receives full 25 points";
        } else {
          return "Complete this measure to receive points";
        }
      }

      if (measure.measureId === "PI_PHCDRR_3") {
        if (phcdrr1Participating && phcdrr3Participating) {
          return "Both Public Health measures completed - PI_PHCDRR_3 redistributes its 12.5 points to PI_PHCDRR_1 (shows 0 points)";
        } else if (phcdrr3Participating && !phcdrr1Participating) {
          return "Only PI_PHCDRR_3 participating - receives full 25 points";
        } else {
          return "Complete this measure to receive points";
        }
      }
    }

    const isActiveAlternativeHie =
      (measure.measureId === "PI_HIE_5" || measure.measureId === "PI_HIE_6") &&
      this.getActiveHieMeasure() === measure.measureId;

    this.measures.forEach((sourceMeasure) => {
      if (!sourceMeasure.selected || !sourceMeasure.exclusionClaimed) return;

      const redistributedPoints = this.getRedistributionTargets(sourceMeasure)
        .filter(
          (target) =>
            target.measureId === measure.measureId ||
            (isActiveAlternativeHie &&
              (target.measureId === "PI_HIE_1" || target.measureId === "PI_HIE_4")),
        )
        .reduce((sum, target) => sum + target.points, 0);

      if (redistributedPoints > 0) {
        sources.push(`+${redistributedPoints} from ${sourceMeasure.measureId} exclusion`);
      }
    });

    const hasExclusionSources = sources.length > 0;

    if (this.isPublicHealthFlatBonusApplied(measure)) {
      sources.push(
        "+25 from PI_PHCDRR_1 and PI_PHCDRR_3 exclusions (flat, not scaled)",
      );
    }

    if (sources.length === 0) return "";

    const scalingNote =
      hasExclusionSources && measure.valueType === "numerator_denominator"
        ? "\nPoints from exclusions are scaled by this measure's performance rate."
        : "";

    if (sources.length === 1) return sources[0] + scalingNote;

    const total = this.getRedistributedPointsAmount(measure);
    return `Bonus Points (Total: +${total}):\n${sources.join("\n")}${scalingNote}`;
  }

  private getRedistributionTargets(
    sourceMeasure: PiMeasure,
  ): { measureId: string; points: number }[] {
    const redistribution = sourceMeasure.pointRedistribution;
    if (!redistribution) return [];

    const directTargets: { measureId: string; points: number }[] =
      redistribution.targetMeasureId
        ? [{ measureId: redistribution.targetMeasureId, points: redistribution.points ?? 0 }]
        : (redistribution.targetMeasureIds ?? []).map((targetMeasureId: string) => ({
            measureId: targetMeasureId,
            points: redistribution.pointsEach ?? 0,
          }));

    return directTargets.flatMap((target) => {
      const targetMeasure = this.measures.find(
        (candidate) => candidate.measureId === target.measureId,
      );
      const passThroughTargets = targetMeasure?.selected && targetMeasure.exclusionClaimed
        ? this.getRedistributionTargets(targetMeasure)
        : [];
      if (passThroughTargets.length === 0) return [target];

      const pointsEach = target.points / passThroughTargets.length;
      return passThroughTargets.map((passThroughTarget) => ({
        measureId: passThroughTarget.measureId,
        points: pointsEach,
      }));
    });
  }

  isSuspendedMeasure(measureId: string): boolean {
    const suspendedMeasures = ["PI_PHCDRR_3"];
    return suspendedMeasures.includes(measureId);
  }

  getSuspendedMeasureTooltip(measureId: string): string {
    if (measureId === "PI_PHCDRR_3") {
      return "Suppressed measure - Full credit awarded regardless of response";
    }
    return "Suppressed measure for 2025 - Full credit awarded";
  }

  // ============================================================================
  // MEASURE CHANGE HANDLERS
  // ============================================================================

  onMeasureCheckboxChange(measure: PiMeasure): void {
    if (this.isHieMeasureDisabled(measure) && measure.selected) {
      measure.selected = false;
      return;
    }

    if (measure.selected) {
      this.handleHieMutualExclusivity(measure);
    }

    if (!measure.selected) {
      this.clearMeasureData(measure);
    }

    this.recalculateAndUpdate();
  }

  private handleHieMutualExclusivity(measure: PiMeasure): void {
    if (measure.measureId === "PI_HIE_5") {
      ["PI_HIE_1", "PI_HIE_4", "PI_HIE_6"].forEach((id) => {
        const m = this.measures.find((m) => m.measureId === id);
        if (m) this.clearMeasureData(m);
      });
    } else if (measure.measureId === "PI_HIE_6") {
      ["PI_HIE_1", "PI_HIE_4", "PI_HIE_5"].forEach((id) => {
        const m = this.measures.find((m) => m.measureId === id);
        if (m) this.clearMeasureData(m);
      });
    } else if (
      measure.measureId === "PI_HIE_1" ||
      measure.measureId === "PI_HIE_4"
    ) {
      ["PI_HIE_5", "PI_HIE_6"].forEach((id) => {
        const m = this.measures.find((m) => m.measureId === id);
        if (m) this.clearMeasureData(m);
      });
    }
  }

  private clearMeasureData(measure: PiMeasure): void {
    measure.selected = false;
    measure.attestation = undefined;
    measure.numerator = undefined;
    measure.denominator = undefined;
    measure.performance = 0;
    measure.activeEngagementLevel = null;
    measure.exclusionClaimed = null;
    measure.completed = false;
    this.measureValidationErrors.delete(measure.measureId);
  }

  onMeasureChange(measure: PiMeasure): void {
    this.updateMeasureCompletion(measure);
    this.recalculateAndUpdate();
  }

  onNumeratorChange(measure: PiMeasure, value: number): void {
    measure.numerator = value;

    this.validateNumeratorDenominator(measure);

    if (!value || !measure.denominator) {
      measure.performance = 0;
    }
    this.updateMeasureCompletion(measure);
    this.recalculateAndUpdate();
  }

  onDenominatorChange(measure: PiMeasure, value: number): void {
    measure.denominator = value;

    this.validateNumeratorDenominator(measure);

    if (!value || !measure.numerator) {
      measure.performance = 0;
    }
    this.updateMeasureCompletion(measure);
    this.recalculateAndUpdate();
  }

  onAttestationChange(measure: PiMeasure, value: boolean): void {
    measure.attestation = value;

    if (!value && measure.valueType === "yes_no_engagement") {
      measure.activeEngagementLevel = null;
    }

    if (value && measure.exclusionClaimed) {
      measure.exclusionClaimed = null;
    }

    this.updateMeasureCompletion(measure);
    this.recalculateAndUpdate();
  }

  onEngagementLevelChange(
    measure: PiMeasure,
    level: ActiveEngagementLevel,
  ): void {
    if (measure.exclusionClaimed) {
      measure.exclusionClaimed = null;
    }
    measure.activeEngagementLevel = level;
    measure.attestation = true;
    measure.completed = true;
    this.recalculateAndUpdate();
  }

  onExclusionClaim(measure: PiMeasure, exclusionId: string): void {
    if (measure.exclusionClaimed === exclusionId) {
      measure.exclusionClaimed = null;
      measure.completed = false;
    } else {
      measure.exclusionClaimed = exclusionId;
      measure.completed = true;

      if (measure.valueType === "yes_no_engagement") {
        measure.activeEngagementLevel = null;
        measure.attestation = undefined;
      }
    }

    this.recalculateAndUpdate();
  }

  private recalculateAndUpdate(): void {
    this.validatePdmpExclusionRule();
    this.updateCategoryCompletionStatus();
    this.updateParentFormValidity();
    this.measureChanged$.next();
  }

  // ============================================================================
  // DATE PICKER HANDLERS
  // ============================================================================

  dateFilter = (date: Date | null): boolean => {
    if (!date) return true;
    return date >= this.minDate && date <= this.maxDate;
  };

  validateDateInRange(date: Date | null): boolean {
    if (!date) return false;
    const isValid = date >= this.minDate && date <= this.maxDate;
    return isValid;
  }

  validateDateRange(): void {
    this.dateRangeInvalid = false;
    this.startDateBeforeEndDateError = false;

    if (!this.performanceStartDate || !this.performanceEndDate) {
      return;
    }

    if (this.performanceStartDate > this.performanceEndDate) {
      this.startDateBeforeEndDateError = true;
      this.dateRangeInvalid = true;
    }
  }

  onPerformanceStartDateChange(date: Date | null): void {
    if (date && !this.validateDateInRange(date)) {
      this.performanceStartDateInvalid = true;
      this.performanceStartDate = date;
      this.updateParentFormValidity();
      this.cdr.markForCheck();
      return;
    }

    this.performanceStartDateInvalid = false;
    this.performanceStartDate = date;

    this.validateDateRange();

    setTimeout(() => {
      this.updateParentFormValidity();
      this.measureChanged$.next();
    });
  }

  onPerformanceEndDateChange(date: Date | null): void {
    if (date && !this.validateDateInRange(date)) {
      this.performanceEndDateInvalid = true;
      this.performanceEndDate = date;
      this.updateParentFormValidity();
      this.cdr.markForCheck();
      return;
    }

    this.performanceEndDateInvalid = false;
    this.performanceEndDate = date;

    this.validateDateRange();

    setTimeout(() => {
      this.updateParentFormValidity();
      this.measureChanged$.next();
    });
  }

  // ============================================================================
  // CATEGORY MANAGEMENT
  // ============================================================================

  toggleCategory(category: PiCategoryGroup): void {
    category.isExpanded = !category.isExpanded;
  }

  shouldExpandCategory(category: PiCategoryGroup): boolean {
    return category.category === "attestations";
  }

  categoryHasBlockingMeasures(category: PiCategoryGroup): boolean {
    return category.measures.some((m) => m.isBlocking);
  }

  categoryHasBonusMeasures(category: PiCategoryGroup): boolean {
    return category.measures.some((m) => m.isBonusMeasure);
  }

  // ============================================================================
  // MODALS
  // ============================================================================

  openAttestationModal(): void {
    const dialogRef = this.dialog.open(PiAttestationModalComponent, {
      width: "900px",
      maxWidth: "95vw",
      maxHeight: "90vh",
      disableClose: false,
      data: {
        ...this.attestationData,
        performanceYear: this.currentPacket()?.performanceYear ?? 2025,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.attestationData = result;
        this.updateParentFormValidity();
        this.savePiAttestations(result);
      }
    });
  }

  openExclusionModal(measure: PiMeasure): void {
    if (!measure.exclusions || measure.exclusions.length === 0) return;

    const dialogRef = this.dialog.open(PiExclusionModalComponent, {
      width: "600px",
      disableClose: false,
      data: {
        measureTitle: measure.measureTitle,
        exclusions: measure.exclusions,
        selectedExclusionId: measure.exclusionClaimed,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined) {
        if (result === null) {
          measure.exclusionClaimed = null;
          measure.completed = false;
        } else {
          measure.exclusionClaimed = result;
          measure.completed = true;
          measure.attestation = undefined;
          measure.activeEngagementLevel = null;
        }

        this.recalculateAndUpdate();
      }
    });
  }

  clearExclusion(measure: PiMeasure): void {
    measure.exclusionClaimed = null;
    measure.completed = false;

    if (
      measure.valueType === "yes_no" ||
      measure.valueType === "yes_no_engagement" ||
      measure.valueType === "attestation"
    ) {
      measure.attestation = undefined;
    }

    if (measure.valueType === "yes_no_engagement") {
      measure.activeEngagementLevel = null;
    }

    this.recalculateAndUpdate();
  }

  openPdf(measure: PiMeasure): void {
    if (!measure.pdfUrl) {
      return;
    }

    this.mipsApiService
      .viewPiMeasurePdf(measure.pdfUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          openBlobInNewTab(blob);
        },
        error: () => {
          this.snackBar.open(
            "Failed to open the reference PDF. Please try again.",
            "Dismiss",
            {
              duration: 5000,
              horizontalPosition: "center",
              verticalPosition: "top",
              panelClass: ["error-snackbar"],
            },
          );
        },
      });
  }

  // ============================================================================
  // FILE UPLOAD
  // ============================================================================

  toggleFileUploadSection(): void {
    this.fileUploadExpanded = !this.fileUploadExpanded;
  }

  onFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files[0]) return;

    const file = input.files[0];
    const maxSize = MAX_FILE_SIZE;

    if (file.size > maxSize) {
      alert("File size must be less than 25MB");
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type)) {
      alert("Only PDF, JPG, and PNG files are allowed");
      return;
    }

    const packet = this.currentPacket();
    if (!packet?.id) {
      alert("No submission found");
      return;
    }

    this.store.dispatch(
      MipsPacketActions.uploadPIDocument({
        packetId: packet.id,
        file: file,
      }),
    );

    input.value = "";
  }

  viewFile(doc: any): void {
    const packet = this.currentPacket();
    if (!packet?.id) return;

    this.store.dispatch(
      MipsPacketActions.viewPIDocument({
        packetId: packet.id,
        documentId: doc.documentId,
        documentName: doc.fileName,
      }),
    );
  }

  downloadFile(doc: any): void {
    const packet = this.currentPacket();
    if (!packet?.id) return;

    this.store.dispatch(
      MipsPacketActions.downloadPIDocument({
        packetId: packet.id,
        documentId: doc.documentId,
        documentName: doc.fileName,
        asAttachment: true,
      }),
    );
  }

  removeUploadedFile(documentId: number): void {
    const packet = this.currentPacket();
    if (!packet?.id) return;

    const doc = this.piDocuments.find((d) => d.documentId === documentId);
    const documentName = doc?.fileName || "this document";

    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: "450px",
      data: {
        title: "Delete Document",
        content: `Are you sure you want to delete "${documentName}"? This action cannot be undone.`,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.store.dispatch(
          MipsPacketActions.deletePIDocument({
            packetId: packet.id!,
            documentId: documentId,
          }),
        );
      }
    });
  }

  // ============================================================================
  // SAVE TO BACKEND
  // ============================================================================

  private savePiData(): void {
    const packet = this.currentPacket();

    if (!packet?.id) {
      return;
    }

    if (!this.performanceStartDate || !this.performanceEndDate) {
      this.snackBar.open(
        "Cannot save: Performance dates are required",
        "Dismiss",
        {
          duration: 5000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        },
      );
      return;
    }

    if (this.performanceStartDateInvalid || this.performanceEndDateInvalid) {
      this.snackBar.open(
        `Cannot save: Performance dates must be within ${packet!.performanceYear}`,
        "Dismiss",
        {
          duration: 5000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        },
      );
      return;
    }

    if (this.dateRangeInvalid || this.startDateBeforeEndDateError) {
      this.snackBar.open(
        "Cannot save: Start date must be before end date",
        "Dismiss",
        {
          duration: 5000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        },
      );
      return;
    }

    const performanceStart = this.normalizeDateForSave(
      this.performanceStartDate,
    );
    const performanceEnd = this.normalizeDateForSave(this.performanceEndDate);

    const measurements = this.measures
      .filter((m) => {
        return m.selected === true;
      })
      .map((m) => {
        const measurement: any = {
          measureId: m.exclusionClaimed || m.measureId,
          valueType: this.getValueTypeForApi(m.valueType),
        };

        if (m.exclusionClaimed) {
          measurement.valueType = "Boolean";
          measurement.booleanValue = true;
          return measurement;
        }

        switch (m.valueType) {
          case "yes_no":
          case "attestation":
            measurement.booleanValue = m.attestation;
            break;

          case "numerator_denominator":
            measurement.numerator = m.numerator ?? 0;
            measurement.denominator = m.denominator ?? 0;
            break;

          case "yes_no_engagement":
            if (m.activeEngagementLevel) {
              measurement.booleanValue = true;
              measurement.activeEngagementLevel = m.activeEngagementLevel;
            }
            break;
        }

        return measurement;
      });

    const fullMeasures = this.measures.filter((m) => m.selected === true).map((m) => ({ ...m }));

    this.dataLoaded = true;
    this.loadedPacketId = packet.id;

    this.store.dispatch(
      MipsPacketActions.savePIMeasurements({
        packetId: packet.id,
        performanceStart: performanceStart!,
        performanceEnd: performanceEnd!,
        measurements,
        fullMeasures,
        exclusionIds: this.getClaimedExclusionIds(),
      }),
    );
  }

  private getClaimedExclusionIds(): string[] {
    return this.measures
      .filter((measure) => measure.selected && measure.exclusionClaimed)
      .map((measure) => measure.exclusionClaimed!);
  }

  private normalizeDateForSave(date: Date | null): Date | null {
    if (!date) return null;

    const normalized = new Date(date);

    if (isNaN(normalized.getTime()) || normalized.getFullYear() < 1900 || normalized.getFullYear() > 2100) {
      return null;
    }

    normalized.setHours(12, 0, 0, 0);

    return normalized;
  }

  private savePiAttestations(attestationData: any): void {
    const packet = this.currentPacket();
    if (!packet?.id) return;

    if (!this.performanceStartDate || !this.performanceEndDate) {
      this.snackBar.open(
        "Cannot save attestations: Performance dates are required",
        "Dismiss",
        {
          duration: 5000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        },
      );
      return;
    }

    if (this.performanceStartDateInvalid || this.performanceEndDateInvalid) {
      this.snackBar.open(
        `Cannot save: Performance dates must be within ${packet!.performanceYear}`,
        "Dismiss",
        {
          duration: 5000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        },
      );
      return;
    }

    const organizationId = parseInt(packet.orgId, 10);
    const performanceYear = packet.performanceYear;

    const attestations = {
      piAuditAttestation: attestationData.piAuditAttestation === true,
      oncDirectReview: attestationData.oncDirectReview,
      interoperabilityAttestation: attestationData.interoperabilityAttestation,
      securityRiskAnalysis: attestationData.securityRiskAnalysis,
      securityRiskAnalysisImplemented: attestationData.securityRiskAnalysisImplemented ?? null,
      saferGuidesAttestation: attestationData.saferGuidesAttestation,
    };

    const normalizedStart = this.normalizeDateForSave(this.performanceStartDate);
    const normalizedEnd = this.normalizeDateForSave(this.performanceEndDate);

    const currentMeasures = this.measures
      .filter((m) => m.selected === true)
      .map((m) => {
        const measurement: any = {
          measureId: m.exclusionClaimed || m.measureId,
          valueType: this.getValueTypeForApi(m.valueType),
        };

        if (m.exclusionClaimed) {
          measurement.valueType = "Boolean";
          measurement.booleanValue = true;
          return measurement;
        }

        switch (m.valueType) {
          case "yes_no":
          case "attestation":
            measurement.booleanValue = m.attestation;
            break;

          case "numerator_denominator":
            measurement.numerator = m.numerator ?? 0;
            measurement.denominator = m.denominator ?? 0;
            break;

          case "yes_no_engagement":
            if (m.activeEngagementLevel) {
              measurement.booleanValue = true;
              measurement.activeEngagementLevel = m.activeEngagementLevel;
            }
            break;
        }

        return measurement;
      });

    this.dataLoaded = true;
    this.loadedPacketId = packet.id;

    this.store.dispatch(
      MipsPacketActions.savePIAttestations({
        packetId: packet.id,
        attestations,
        performanceStart: normalizedStart!,
        performanceEnd: normalizedEnd!,
        organizationId,
        performanceYear,
        currentMeasures,
        fullMeasures: this.measures.filter((measure) => measure.selected === true).map((measure) => ({ ...measure })),
        exclusionIds: this.getClaimedExclusionIds(),
      }),
    );
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  private getValueTypeForApi(valueType: string): string {
    switch (valueType) {
      case "numerator_denominator":
        return "Proportion";
      case "yes_no":
      case "yes_no_engagement":
      case "attestation":
        return "Boolean";
      default:
        return "Boolean";
    }
  }

  private formatDate(date: string | Date): string {
    if (!date) return "";
    const d = new Date(date);
    return `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d
      .getDate()
      .toString()
      .padStart(2, "0")}/${d.getFullYear()}`;
  }
}
